/**
 * /callingpros — invite legends to claim badges held by Admin AI.
 */
import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useWalletState } from "@/contexts/wallet-context";
import { CALLING_PROS } from "@shared/calling-pros";
import { ProBadgeRing } from "@/components/pro-badge-ring";
import { BRAND } from "@shared/brand";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useDappSign } from "@/contexts/dapp-sign-context";
import { signedBodyFields, type SignedChallenge } from "@/lib/signed-action";
import "@/styles/calling-pros.css";

type ProRow = {
  id: string;
  name: string;
  icon: string;
  tagline: string;
  status: "held_by_admin_ai" | "claimed";
  holder?: {
    wallet?: string;
    walletHint?: string;
    username?: string | null;
    label?: string;
    note?: string;
  };
};

export default function CallingProsPage() {
  const [, setLocation] = useLocation();
  const { wallet } = useWalletState();
  const { requestSign } = useDappSign();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const pk = wallet.publicKey;

  const listQ = useQuery({
    queryKey: ["calling-pros"],
    queryFn: async () => {
      const r = await fetch("/api/calling-pros");
      if (!r.ok) throw new Error("Failed to load pros");
      return r.json() as Promise<{ icons: ProRow[]; adminHold: { label: string; note: string } }>;
    },
    refetchInterval: 20000,
  });

  const meQ = useQuery({
    queryKey: ["calling-pros-me", pk],
    enabled: !!pk,
    queryFn: async () => {
      const r = await fetch(
        `/api/calling-pros/me?wallet=${encodeURIComponent(pk!)}`,
        { headers: { "X-Wallet-Address": pk! } },
      );
      if (!r.ok) throw new Error("me failed");
      return r.json() as Promise<{ claim: { proId: string } | null }>;
    },
  });

  const icons = listQ.data?.icons ?? CALLING_PROS.map((p) => ({
    ...p,
    status: "held_by_admin_ai" as const,
    holder: { label: "Admin AI", note: "Holds until claimed" },
  }));

  const coinStrip = useMemo(
    () => CALLING_PROS.slice(0, 12).map((p) => p.coin),
    [],
  );

  const myPro = meQ.data?.claim?.proId;

  const claim = async (proId: string) => {
    if (!pk) {
      toast({
        title: "Connect wallet",
        description: "Pros claim badges with their Solana wallet.",
        variant: "destructive",
      });
      setLocation("/connect");
      return;
    }
    setBusy(proId);
    try {
      const signed = await requestSign({
        purpose: "pro_claim",
        action: "pro_claim",
        params: { proId },
        details: [
          { label: "Badge", value: proId },
          { label: "Wallet", value: `${pk.slice(0, 4)}…${pk.slice(-4)}` },
        ],
      });
      const r = await fetch("/api/calling-pros/claim", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Wallet-Address": pk,
        },
        body: JSON.stringify({
          wallet: pk,
          proId,
          username: undefined,
          ...signedBodyFields({
            message: signed.message,
            signature: signed.signature,
            nonce: signed.nonce || "",
            action: "pro_claim",
          } as SignedChallenge),
        }),
      });
      const j = (await r.json()) as { error?: string; message?: string; proId?: string };
      if (!r.ok) throw new Error(j.error || "Claim failed");
      toast({
        title: "Badge claimed",
        description: j.message || "Admin AI released the hold. Wear it at the tables.",
      });
      void qc.invalidateQueries({ queryKey: ["calling-pros"] });
      void qc.invalidateQueries({ queryKey: ["calling-pros-me"] });
    } catch (e) {
      toast({
        title: "Claim failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="calling-pros-page">
      <div className="calling-pros-ambient" aria-hidden />
      <div className="calling-pros-shell">
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <Button
            variant="outline"
            size="sm"
            className="border-amber-500/30"
            onClick={() => setLocation("/")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Home
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-700"
            onClick={() => setLocation("/lobby")}
          >
            Lobby
          </Button>
        </div>

        <header className="calling-pros-hero">
          <div className="calling-pros-coins" aria-hidden>
            {coinStrip.map((src, i) => (
              <img key={i} src={src} alt="" />
            ))}
          </div>
          <h1>Calling Pros</h1>
          <p>
            Twenty-five legendary badges for the felt.{" "}
            <strong style={{ color: "#f0c14b" }}>Admin AI</strong> holds every
            icon until a pro player claims it — then the animated circle lights
            up behind your seat at the table.
          </p>
          <div className="calling-pros-banner">
            <Sparkles className="inline h-3.5 w-3.5 mr-1 text-amber-300" />
            <strong>How it works:</strong> Connect wallet → claim one badge →
            join a table. Unclaimed icons stay with Admin AI (cool ring + AI
            tag). Claimed badges deploy only to that pro&apos;s wallet.
            {myPro && (
              <>
                {" "}
                You hold: <strong>{myPro}</strong>
              </>
            )}
          </div>
          <img
            src={BRAND.logoMark}
            alt=""
            width={56}
            height={56}
            style={{ borderRadius: 12, marginTop: 4 }}
          />
        </header>

        <div className="calling-pros-grid">
          {icons.map((p, i) => {
            const held = p.status === "held_by_admin_ai";
            const isMine = myPro === p.id;
            return (
              <article
                key={p.id}
                className={`calling-pro-card ${held ? "" : "is-claimed"}`}
              >
                <ProBadgeRing
                  proId={p.id}
                  seatIndex={i}
                  size={88}
                  adminHold={held}
                  claimed={!held}
                />
                <h3>{p.name}</h3>
                <p className="tag">{p.tagline}</p>
                <span className={`status ${held ? "held" : "claimed"}`}>
                  {held
                    ? "Admin AI holds"
                    : isMine
                      ? "Yours"
                      : p.holder?.username || p.holder?.walletHint || "Claimed"}
                </span>
                {held ? (
                  <button
                    type="button"
                    className="claim"
                    disabled={!!busy || (!!myPro && myPro !== p.id)}
                    onClick={() => void claim(p.id)}
                  >
                    {busy === p.id
                      ? "Claiming…"
                      : myPro && myPro !== p.id
                        ? "You have a badge"
                        : "Claim badge"}
                  </button>
                ) : (
                  <button type="button" className="ghost" disabled>
                    {isMine ? "Deployed to you" : "Deployed"}
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
